// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useMemo } from 'react'
import type {
    AICreateVariantStatus,
    GetAICreateSessionResponse,
} from '@storefronts/common-types'
import { AI_CREATE_VARIANT_STATUS } from '@storefronts/common-types'
import { useAIChatContext, type PreviewData } from './AIChatContext'
import { useAiCreateRunSignals } from './AiCreateRunSignals'

const VARIANT_STAGE_LABELS: Partial<Record<AICreateVariantStatus, string>> = {
    [AI_CREATE_VARIANT_STATUS.GENERATED]: 'content written',
    [AI_CREATE_VARIANT_STATUS.REVIEWING]: 'content written',
    [AI_CREATE_VARIANT_STATUS.REVISING]: 'review fixes',
    [AI_CREATE_VARIANT_STATUS.DONE]: 'finished',
    [AI_CREATE_VARIANT_STATUS.FAILED]: 'stopped',
}

/**
 * Each pipeline stage rewrites the page. Content-node finishes reload the
 * preview before variant status is published; variant status and edit notes
 * cover the review stages after that.
 */
export function previewRefreshSignals(
    snapshot: GetAICreateSessionResponse | null,
    isComplete: boolean,
    editNoteCount: number,
    contentStageCount: number,
    contentStageLabel: string | null,
): Pick<PreviewData, 'refreshToken' | 'refreshLabel'> {
    const primaryStatus = (
        snapshot?.variants?.find((variant) => variant.isPrimary) ??
        snapshot?.variants?.[0]
    )?.status
    const variantLabel = primaryStatus && VARIANT_STAGE_LABELS[primaryStatus]
    return {
        refreshToken: `${primaryStatus ?? ''}:${isComplete}:${editNoteCount}:${contentStageCount}`,
        refreshLabel: isComplete
            ? 'finished'
            : (variantLabel ?? contentStageLabel ?? 'refining content'),
    }
}

// A freshly-duplicated draft is just the template skeleton. The preview stays
// hidden until generateLlmContent finishes on the stream. Variant status only
// reads content-ready after image curation, so it is a late fallback when that
// tool result was missed on replay.
const CONTENT_READY_VARIANT_STATUSES = new Set<AICreateVariantStatus>([
    AI_CREATE_VARIANT_STATUS.GENERATED,
    AI_CREATE_VARIANT_STATUS.REVIEWING,
    AI_CREATE_VARIANT_STATUS.REVISING,
    AI_CREATE_VARIANT_STATUS.DONE,
])

export function isPreviewReady(
    snapshot: GetAICreateSessionResponse | null,
    isComplete: boolean,
    contentReady: boolean,
): boolean {
    if (isComplete || contentReady) {
        return true
    }
    const primary =
        snapshot?.variants?.find((variant) => variant.isPrimary) ??
        snapshot?.variants?.[0]
    return !!primary && CONTENT_READY_VARIANT_STATUSES.has(primary.status)
}

/**
 * Storefront preview derived from the session snapshot: non-null once a page
 * hash exists AND content has been written, marked "refining" until the
 * primary polish finishes.
 */
export function useActivePreview() {
    const { state } = useAIChatContext()
    const {
        editNoteCount,
        contentReady,
        contentStageCount,
        contentStageLabel,
    } = useAiCreateRunSignals()

    const activePreview = useMemo((): PreviewData | null => {
        const finalHash = state.snapshot?.finalLandingPageHash
        if (
            !finalHash ||
            !isPreviewReady(state.snapshot, state.isComplete, contentReady)
        ) {
            return null
        }
        return {
            finalHash,
            imageCurationComplete: state.isComplete,
            ...previewRefreshSignals(
                state.snapshot,
                state.isComplete,
                editNoteCount,
                contentStageCount,
                contentStageLabel,
            ),
        }
    }, [
        state.snapshot,
        state.isComplete,
        editNoteCount,
        contentReady,
        contentStageCount,
        contentStageLabel,
    ])

    return { previewData: activePreview }
}
